import React from "react";
import Plot, { PLOT_CONFIG, PLOT_FONT } from "./plotly";
import { CONDITION_ANNOTATION, CONDITION_ORDER, styleFor } from "../lib/conditions";
import { ic50Annotation } from "../lib/format";

/**
 * The concentration-response figure, on the same axes as the exported one:
 * log10 concentration against percent of control, the conditions overlaid,
 * IC50 annotated per condition, outliers struck through.
 *
 * Takes a list of series rather than a compound, so the positive controls --
 * which are two different compounds, one per condition -- draw on one figure the
 * same way the exported STD figure draws them.
 *
 * Clicking a point selects it, and the point the table is hovering is ringed --
 * that pairing is what makes the table and the curve one view rather than two.
 *
 * A point marked as an outlier is off the figure by default: it is not part of
 * the fit, so drawing it invites the eye to judge the curve against points the
 * curve never saw. It is still in the table, flagged, and `showOutliers` puts it
 * back on the figure crossed and greyed for the moment someone wants to look at
 * what was removed. `hiddenConditions` does the same for a whole condition, so
 * one curve can be read without the other two over it.
 */
export default function CurveChart({
  series: allSeries,
  title,
  highlighted,
  selected = [],
  showOutliers = false,
  hiddenConditions = [],
  onPointClick,
}) {
  const ordered = [...allSeries]
    .filter((s) => !hiddenConditions.includes(s.condition))
    .sort((a, b) => CONDITION_ORDER.indexOf(a.condition) - CONDITION_ORDER.indexOf(b.condition));

  const data = [];
  const annotations = [];
  let highest = 100;

  ordered.forEach((series) => {
    const style = styleFor(series.condition);
    const label = `${series.compound} ${series.condition}`;

    if (series.fit?.curve_x?.length) {
      data.push({
        x: series.fit.curve_x,
        y: series.fit.curve_y,
        type: "scatter",
        mode: "lines",
        line: { color: style.color, width: 2 },
        name: label,
        legendgroup: series.condition,
        hoverinfo: "skip",
      });
    }

    const included = series.observations.filter((o) => o.included && o.percent_of_control != null);
    const excluded = series.observations.filter((o) => !o.included && o.percent_of_control != null);
    // From what is drawn, so hiding an outlier that sat at 140 % brings the axis
    // back down rather than leaving a stretch of empty plot behind it.
    [...included, ...(showOutliers ? excluded : [])].forEach((o) => {
      if (o.percent_of_control > highest) highest = o.percent_of_control;
    });

    data.push({
      x: included.map((o) => o.concentration_um),
      y: included.map((o) => o.percent_of_control),
      customdata: included.map((o) => [o.observation_id, o.sample_name]),
      type: "scatter",
      mode: "markers",
      marker: {
        color: style.color,
        size: 9,
        symbol: style.symbol,
        line: {
          width: included.map((o) => (selected.includes(o.observation_id) ? 3 : 0)),
          color: "#1a1d21",
        },
      },
      name: label,
      legendgroup: series.condition,
      showlegend: !series.fit?.curve_x?.length,
      hovertemplate:
        "%{customdata[1]}<br>%{x:.4g} µM<br>%{y:.1f} % of control" +
        "<extra>click to select</extra>",
    });

    if (showOutliers && excluded.length) {
      data.push({
        x: excluded.map((o) => o.concentration_um),
        y: excluded.map((o) => o.percent_of_control),
        customdata: excluded.map((o) => [o.observation_id, o.sample_name]),
        type: "scatter",
        mode: "markers",
        marker: { color: "#9e9e9e", size: 10, symbol: "x", line: { width: 2 } },
        name: `${label} (outlier)`,
        legendgroup: series.condition,
        hovertemplate:
          "%{customdata[1]}<br>%{x:.4g} µM<br>%{y:.1f} % of control" +
          "<extra>outlier — click to unmark</extra>",
      });
    }

    if (series.fit) {
      annotations.push(
        ic50Annotation(CONDITION_ANNOTATION[series.condition], series.fit.ic50_display)
      );
    }
  });

  if (highlighted) {
    // Only ring a point that is on the figure: a ring around nothing, where an
    // outlier would have been, reads as a plotting fault rather than a hover.
    const point = ordered
      .flatMap((s) => s.observations)
      .find((o) => o.observation_id === highlighted && (o.included || showOutliers));
    if (point && point.percent_of_control != null) {
      data.push({
        x: [point.concentration_um],
        y: [point.percent_of_control],
        type: "scatter",
        mode: "markers",
        marker: { size: 18, color: "rgba(0,0,0,0)", line: { width: 2, color: "#1a1d21" } },
        hoverinfo: "skip",
        showlegend: false,
      });
    }
  }

  const layout = {
    title: { text: title, font: { size: 15 } },
    height: 520,
    margin: { l: 64, r: 20, t: 46, b: 56 },
    xaxis: {
      title: "Concentration [µM]",
      type: "log",
      // One tick per decade, as on the reference slides. Plotly's default adds
      // labelled minor ticks, which read as a repeating "2, 5" across the axis.
      dtick: 1,
      gridcolor: "#eef0f2",
      zeroline: false,
      exponentformat: "none",
    },
    yaxis: {
      title: "% of control",
      range: [0, highest > 100 ? 50 * (Math.floor(highest / 50) + 1) : 100],
      gridcolor: "#eef0f2",
      zeroline: false,
    },
    shapes: [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        x1: 1,
        y0: 50,
        y1: 50,
        line: { color: "#bbbbbb", width: 1, dash: "dot" },
      },
    ],
    annotations: annotations.length
      ? [
          {
            xref: "paper",
            yref: "paper",
            x: 0.02,
            y: 0.04,
            xanchor: "left",
            yanchor: "bottom",
            align: "left",
            text: annotations.join("<br>"),
            showarrow: false,
            bgcolor: "rgba(255,255,255,0.9)",
            bordercolor: "#cccccc",
            borderwidth: 1,
            borderpad: 6,
          },
        ]
      : [],
    font: PLOT_FONT,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    legend: { orientation: "h", y: -0.18, font: { size: 11 } },
    hovermode: "closest",
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={PLOT_CONFIG}
      style={{ width: "100%" }}
      useResizeHandler
      onClick={(event) => {
        const id = event?.points?.[0]?.customdata?.[0];
        if (id) onPointClick?.(id);
      }}
    />
  );
}
